import React, { createContext, useContext, useState, ReactNode } from "react";

export interface RecipeStudioState {
  coreRecipeQuery: string;
  selectedPersona: string;
  complexity: number;
  selectedDietaryTags: string[];
  specialRequest: string;
  language: string;        // e.g., "English (UK)" default
  languageCustom: string;  // when "Other" is selected
}

interface RecipeStudioContextType extends RecipeStudioState {
  setCoreRecipeQuery: (v: string) => void;
  setSelectedPersona: (v: string) => void;
  setComplexity: (v: number) => void;
  setSelectedDietaryTags: (v: string[]) => void;
  setSpecialRequest: (v: string) => void;
  setLanguage: (v: string) => void;
  setLanguageCustom: (v: string) => void;
  resetStudio: () => void;
}

const RecipeStudioContext = createContext<RecipeStudioContextType | undefined>(undefined);

export const useRecipeStudio = () => {
  const ctx = useContext(RecipeStudioContext);
  if (!ctx) throw new Error("useRecipeStudio must be used within RecipeStudioProvider");
  return ctx;
};

export const RecipeStudioProvider = ({ children }: { children: ReactNode }) => {
  const [coreRecipeQuery, setCoreRecipeQuery] = useState(() => sessionStorage.getItem('studioCoreRecipeQuery') || "");
  const [selectedPersona, setSelectedPersona] = useState(() => sessionStorage.getItem('studioSelectedPersona') || "");
  const [complexity, setComplexity] = useState(() => {
    const stored = sessionStorage.getItem('studioComplexity');
    return stored ? Number(stored) : 2;
  });
  const [selectedDietaryTags, setSelectedDietaryTags] = useState<string[]>(() => {
    const stored = sessionStorage.getItem('studioSelectedDietaryTags');
    return stored ? JSON.parse(stored) : [];
  });
  const [specialRequest, setSpecialRequest] = useState(() => sessionStorage.getItem('studioSpecialRequest') || "");
  const [language, setLanguage] = useState(() => sessionStorage.getItem('studioLanguage') || "English (UK)");
  const [languageCustom, setLanguageCustom] = useState(() => sessionStorage.getItem('studioLanguageCustom') || "");

  React.useEffect(() => {
    sessionStorage.setItem('studioCoreRecipeQuery', coreRecipeQuery);
  }, [coreRecipeQuery]);
  React.useEffect(() => {
    sessionStorage.setItem('studioSelectedPersona', selectedPersona);
  }, [selectedPersona]);
  React.useEffect(() => {
    sessionStorage.setItem('studioComplexity', String(complexity));
  }, [complexity]);
  React.useEffect(() => {
    sessionStorage.setItem('studioSelectedDietaryTags', JSON.stringify(selectedDietaryTags));
  }, [selectedDietaryTags]);
  React.useEffect(() => {
    sessionStorage.setItem('studioSpecialRequest', specialRequest);
  }, [specialRequest]);
  React.useEffect(() => {
    sessionStorage.setItem('studioLanguage', language);
  }, [language]);
  React.useEffect(() => {
    sessionStorage.setItem('studioLanguageCustom', languageCustom);
  }, [languageCustom]);

  const resetStudio = () => {
    setCoreRecipeQuery("");
    setSelectedPersona("");
    setComplexity(2);
    setSelectedDietaryTags([]);
    setSpecialRequest("");
    setLanguage("English (UK)");
    setLanguageCustom("");
  };

  return (
    <RecipeStudioContext.Provider
      value={{
        coreRecipeQuery,
        setCoreRecipeQuery,
        selectedPersona,
        setSelectedPersona,
        complexity,
        setComplexity,
        selectedDietaryTags,
        setSelectedDietaryTags,
        specialRequest,
        setSpecialRequest,
        language,
        setLanguage,
        languageCustom,
        setLanguageCustom,
        resetStudio,
      }}
    >
      {children}
    </RecipeStudioContext.Provider>
  );
};
